import { useCallback, useMemo, useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../context/StateContext";
import { Movement } from "../../../../../models/movement";
import { ItemJob } from "../../../../../models/sales";
import { Filters, Searches, Sorts } from "../../../../../models/shipeezi.table";
import { StorageBox } from "../../../../../models/storage.box";
import { MapCell } from "../../../../../models/warehouse.mapcell";
import ItemJobService from "../../../../../services/item.job.service";
import OrderService from "../../../../../services/order.service";
import StorageBoxService from "../../../../../services/storage.box.service";
import WarehouseMapCellService from "../../../../../services/warehouse.map.cell.service";
import WarehousesMapsService from "../../../../../services/warehouse.map.service";
import WarehousingService from "../../../../../services/warehousing.service";
import { checkError } from "../../../../../util/checkError";
import { handlesGrid } from "../../../../../util/handlesGrid";

interface PropsMoveStorageBox {
  storageBox: StorageBox;
  mapCell: MapCell;
}

interface PropsMoveItemJob {
  itemJob: ItemJob;
  mapCell: MapCell;
}

export enum TypesOfObjects {
  STORAGE_BOX = "storageBox",
  LOOSE_ITEM = "looseItem",
}

export interface TypeOfObject {
  name: TypesOfObjects;
  value: string;
}

export const useActions = () => {
  const { currentUser, warehouse } = useAppState();
  const { t: translate } = useTranslation();

  const [movementsData, setMovementsData] = useState<Movement[]>([]);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(15);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();
  const [total, setTotal] = useState<number>(0);

  const [storageBoxesInMapCell, setStorageBoxesInMapCell] = useState<StorageBox[]>([]);
  const [itemsJobsInMapCell, setItemsJobsInMapCell] = useState<ItemJob[]>([]);
  const [openConfirmationModal, setOpenConfirmationModal] = useState<boolean>(false);
  const [storageBoxId, setStorageBoxId] = useState<number>();
  const [itemJobId, setItemJobId] = useState<number>();
  const [mapId, setMapId] = useState<number>();

  const { handleCurrentPage, handleFilter, handlePageSize, handleSort, handleSearch } = handlesGrid(
    {
      setFilters,
      setSorting,
      setPage,
      setPageSize,
      setSearches,
    }
  );

  const getBranchesId = useCallback(() => {
    const branches = warehouse?.branches?.map((branch) => branch?.id);

    if (!branches?.length) return [];

    return branches;
  }, [warehouse]);

  const attributes = {
    branchFrom: ["id", "companyId"],
    customer: ["id", "name", "lastName"],
    itemJob: ["id", "description", "volume", "weight"],
    job: ["id", "reference"],
    map: ["id", "name", "description", "isActive"],
    section: ["id", "name", "allowsItemLoose", "allowsStorageBox", "isActive"],
    storageBox: ["id", "number", "storageBoxTypeId", "isActive", "lastMovedAt"],
    storageBoxType: ["id", "name", "capacity", "depth", "height", "width"],
    warehouse: ["id", "name"],
    user: ["id", "name", "lastName"],
    eventType: ["id", "description"],
    storageBoxHistory: ["id", "description", "createdAt"],
    itemHistory: ["id", "comment", "createdAt"],
    storageStatus: ["id", "name"],
  };

  const searchByOrWarehouse = [
    { warehouseToId: warehouse?.id },
    { warehouseFromId: warehouse?.id },
  ];

  const movementsAttributes = {
    user: ["id", "fullName"],
    section: ["id", "name"],
    warehouse: ["id", "name"],
    storageBox: ["id", "number"],
    storageBoxType: ["id", "name"],
    itemJob: ["id", "number", "description"],
  };

  const getMovements = () =>
    useQuery(
      ["movements.list", page, pageSize, searches, filters, sorting, warehouse],
      () =>
        WarehousingService.getAllMovements({
          page,
          pageSize,
          $and: [{ $or: searchByOrWarehouse }, { companyId: currentUser?.companyId }],
          sortBy:
            sorting && sorting?.length > 0
              ? JSON.stringify([...sorting, ["updatedAt", "desc"]])
              : JSON.stringify([["updatedAt", "desc"]]),
          include: JSON.stringify([
            { association: "user", attributes: movementsAttributes.user },
            { association: "sectionFrom", attributes: movementsAttributes.section },
            { association: "sectionTo", attributes: movementsAttributes.section },
            { association: "warehouseFrom", attributes: movementsAttributes.warehouse },
            { association: "warehouseTo", attributes: movementsAttributes.warehouse },
            {
              association: "storageBox",
              attributes: movementsAttributes.storageBox,
              include: {
                association: "storageBoxType",
                attributes: movementsAttributes.storageBoxType,
              },
            },
            {
              association: "itemJob",
              attributes: movementsAttributes.itemJob,
              include: [
                {
                  association: "job",
                  attributes: attributes.job,
                  include: [{ association: "customer", attributes: attributes.customer }],
                },
              ],
            },
          ]),
          searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
          ...filters,
        }),
      {
        enabled: !!currentUser?.companyId && !!warehouse?.id,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data) {
            setMovementsData(response.data);
          }
        },
      }
    );

  const getMovementsCount = () =>
    useQuery(
      ["movements.count", filters, searches, warehouse],
      () =>
        WarehousingService.countMovements({
          companyId: currentUser?.companyId,
          $and: [{ $or: searchByOrWarehouse }, { companyId: currentUser?.companyId }],
          searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
          ...filters,
        }),
      {
        enabled: !!currentUser?.companyId && !!warehouse?.id,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          setTotal(response?.data || 0);
        },
      }
    );

  const getOrders = (searches?: Searches) =>
    OrderService.getAll({
      companyId: currentUser?.companyId,
      userId: currentUser?.id,
      branchFromId: [...getBranchesId()],
      include: JSON.stringify([
        {
          association: "itemsJob",
          attributes: attributes.itemJob,
          include: [
            {
              association: "mapCells",
              include: [
                {
                  association: "map",
                  attributes: attributes.map,
                  include: [
                    {
                      association: "warehouse",
                      attributes: attributes.warehouse,
                    },
                  ],
                },
              ],
            },
          ],
        },
      ]),
      searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
      sortBy: JSON.stringify([["reference", "asc"]]),
    });

  const getStorageBoxes = (searches?: Searches) =>
    StorageBoxService.getAll({
      companyId: currentUser?.companyId,
      isActive: 1,
      searchByOr: JSON.stringify(searches),
      sortBy: JSON.stringify([["number", "asc"]]),
      include: JSON.stringify([
        {
          association: "warehouses",
          include: [
            {
              association: "sections",
              attributes: [...attributes.section],
              include: [
                {
                  association: "maps",
                  attributes: attributes.map,
                },
                {
                  association: "storageBoxTypes",
                  attributes: attributes.warehouse,
                },
              ],
            },
          ],
        },
      ]),
    });

  const getStorageBoxInMapCell = (mapCellId?: number) =>
    useQuery(
      ["getList.storageBoxes.in.mapCell", mapCellId],
      () =>
        StorageBoxService.getAll({
          include: JSON.stringify([
            { association: "mapCells", where: { id: mapCellId }, required: true },
          ]),
        }),
      {
        enabled: !!mapCellId,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data) setStorageBoxesInMapCell(response.data);
        },
      }
    );

  const getItemsJobsInMapCell = (mapCellId?: number) =>
    useQuery(
      ["getList.itemsJobs.in.mapCell", mapCellId],
      () =>
        ItemJobService.getAll({
          include: JSON.stringify([
            { required: true, association: "mapCells", where: { id: mapCellId } },
          ]),
        }),
      {
        onSuccess: (response) => {
          if (response && response.data) setItemsJobsInMapCell(response.data);
        },
      }
    );

  const getStorageBoxById = () =>
    useQuery(
      ["getById.storageBox", storageBoxId],
      () =>
        StorageBoxService.getById(Number(storageBoxId), {
          include: JSON.stringify([
            {
              association: "storageStatus",
              attributes: attributes.storageStatus,
            },
            {
              association: "warehouses",
              attributes: attributes.warehouse,
              include: [
                {
                  association: "sections",
                  attributes: [...attributes.section],
                  include: [
                    {
                      association: "maps",
                      attributes: attributes.map,
                    },
                    {
                      association: "storageBoxTypes",
                      attributes: attributes.warehouse,
                    },
                  ],
                },
              ],
            },
            {
              association: "mapCells",
              include: [
                {
                  association: "map",
                  attributes: attributes.map,
                  include: [
                    { association: "section", attributes: attributes.section },
                    { association: "warehouse", attributes: attributes.warehouse },
                  ],
                },
              ],
            },
            {
              association: "itemsJobs",
              attributes: attributes.itemJob,
              include: [
                {
                  association: "job",
                  attributes: attributes.job,
                  include: [{ association: "customer", attributes: attributes.customer }],
                },
              ],
            },
            {
              association: "storageBoxHistories",
              attributes: attributes.storageBoxHistory,
              include: [{ association: "user", attributes: attributes.user }],
            },
            {
              association: "storageBoxType",
              attributes: attributes.storageBoxType,
            },
          ]),
        }),
      {
        enabled: !!storageBoxId,
      }
    );

  const getItemJobById = () =>
    useQuery(
      ["getById.itemJob", itemJobId],
      () =>
        ItemJobService.getById(Number(itemJobId), {
          include: JSON.stringify([
            {
              association: "job",
              required: true,
              attributes: attributes.job,
              include: [{ association: "customer", attributes: attributes.customer }],
            },
            {
              association: "mapCells",
              include: [
                {
                  association: "map",
                  attributes: attributes.map,
                  include: [
                    { association: "section", attributes: attributes.section },
                    { association: "warehouse", attributes: attributes.warehouse },
                  ],
                },
              ],
            },
            {
              association: "storageBoxes",
              attributes: attributes.storageBox,
              include: [
                {
                  association: "mapCells",
                  include: [
                    {
                      association: "map",
                      attributes: attributes.map,
                      include: [
                        { association: "section", attributes: attributes.section },
                        { association: "warehouse", attributes: attributes.warehouse },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              association: "itemHistories",
              attributes: attributes.itemHistory,
              include: [
                { association: "eventType", attributes: attributes.eventType },
                { association: "user", attributes: attributes.user },
              ],
            },
          ]),
        }),
      {
        enabled: !!itemJobId,
      }
    );

  const getMapById = () =>
    useQuery(
      ["getById.map", mapId],
      () =>
        WarehousesMapsService.getById(Number(mapId), {
          include: JSON.stringify([
            {
              association: "mapCells",
              include: [
                { association: "status" },
                { association: "itemsJobs" },
                { association: "storageBoxes" },
              ],
            },
            { association: "configColumn" },
            { association: "configRow" },
            { association: "configLevel" },
          ]),
        }),
      {
        enabled: !!mapId,
      }
    );

  const getMapCells = (name?: string, sectionId?: number, mapId?: number) =>
    WarehouseMapCellService.getAll({
      include: JSON.stringify([
        { association: "map", require: true, where: { sectionId, isActive: true } },
      ]),
      isActive: 1,
      mapId,
      searchByOr: JSON.stringify([{ name: name || "" }]),
      sortBy: JSON.stringify([["name"], ["map", "name", "asc"]]),
    });

  const moveStorageBox = (callback?: () => void) =>
    useMutation(
      ["moveStorageBox"],
      ({ mapCell, storageBox }: PropsMoveStorageBox) =>
        WarehousingService.movement({ mapCell, storageBox }),
      {
        onSuccess: () => {
          NotificationToast.success(translate("operationPerformedSuccessfully"));
          setOpenConfirmationModal(false);
          if (callback) callback();
        },
        onError: (err: AxiosError<Error>) => {
          checkError(err, translate("anErrorOccurredTryAgain"));
        },
      }
    );

  const moveItemJob = (callback?: () => void) =>
    useMutation(
      ["moveItemJob"],
      ({ mapCell, itemJob }: PropsMoveItemJob) => WarehousingService.movement({ mapCell, itemJob }),
      {
        onSuccess: () => {
          NotificationToast.success(translate("operationPerformedSuccessfully"));
          setOpenConfirmationModal(false);
          if (callback) callback();
        },
        onError: (err: AxiosError<Error>) => {
          checkError(err, translate("anErrorOccurredTryAgain"));
        },
      }
    );

  return {
    getItemJobById,
    getOrders,
    getItemsJobsInMapCell,
    getStorageBoxById,
    getStorageBoxes,
    getStorageBoxInMapCell,
    itemsJobsInMapCell,
    moveItemJob,
    moveStorageBox,
    openConfirmationModal,
    setOpenConfirmationModal,
    storageBoxesInMapCell,
    setStorageBoxId,
    storageBoxId,
    setItemJobId,
    itemJobId,
    getMapById,
    getMapCells,
    setMapId,
    page,
    handleCurrentPage,
    pageSize,
    handlePageSize,
    getMovements,
    movementsData,
    total,
    getMovementsCount,
    handleSort,
    handleSearch,
    setSorting,
    handleFilter,
  };
};
